import {
    Bounds,
    CONTACT,
    CLOUD,
    HORIZON,
    Land,
    MOON_HATCH,
    MOON_RIM,
    MOON_TERMINATOR,
    Pt,
    ROCK_BOW,
    ROCK_BOW_HATCH,
    ROCK_NEAR,
    ROCK_SHORE_A,
    ROCK_SHORE_B,
    ROCK_SHORE_HATCH,
    SCENE,
    SHIP_AGROUND,
    SHIP_PARTS,
    ShipState,
    clamp01,
    cloudPoints,
    easeInOutCubic,
    easeInOutSine,
    easeOutCubic,
    flat,
    lanternAt,
    lerp,
    prog,
    shipPoint,
    shipStateAt,
    smooth,
    sprayAt,
} from "./shipwreck_geometry"

/**
 * Paints the shipwreck's line art with Canvas2D, in the scene's authored coordinates.
 *
 * Why Canvas2D and not pixi Graphics: the game's renderer is built without antialiasing, and thin
 * diagonal strokes -- the rigging, the hatching -- stair-step and shimmer as they move. Canvas2D
 * strokes are antialiased, so the art is painted here and handed to pixi as textures (see
 * shipwreck_scene.ts, which also owns which layer is repainted when).
 */

export const INK = "rgb(233, 222, 198)"

/** The night sky over 0..460, top to bottom. Every knock-out fill uses the same gradient. */
const SKY_STOPS: [number, string][] = [
    [0, "#07090d"],
    [0.55, "#0c1017"],
    [0.678, "#131822"],
    [0.7, "#0d1118"],
    [1, "#06080b"],
]

export interface Pen {
    ctx: CanvasRenderingContext2D
    sky: CanvasGradient
}

export const skyGradient = (ctx: CanvasRenderingContext2D): CanvasGradient => {
    const gradient = ctx.createLinearGradient(0, 0, 0, 460)
    SKY_STOPS.forEach(([offset, color]) => gradient.addColorStop(offset, color))
    return gradient
}

/** Draw a polyline up to `frac` of its length. Returns true when it reached the end. */
const trace = (ctx: CanvasRenderingContext2D, pts: Pt[], frac: number, closed: boolean): boolean => {
    const n = pts.length
    const segments = closed ? n : n - 1
    const lengths: number[] = []
    let total = 0
    for (let j = 0; j < segments; j++) {
        const a = pts[j], b = pts[(j + 1) % n]
        const l = Math.hypot(b[0] - a[0], b[1] - a[1])
        lengths.push(l)
        total += l
    }
    let remain = total * frac
    ctx.moveTo(pts[0][0], pts[0][1])
    for (let j = 0; j < segments; j++) {
        const a = pts[j], b = pts[(j + 1) % n]
        if (lengths[j] <= remain) {
            ctx.lineTo(b[0], b[1])
            remain -= lengths[j]
        } else {
            const u = lengths[j] ? remain / lengths[j] : 0
            ctx.lineTo(a[0] + (b[0] - a[0]) * u, a[1] + (b[1] - a[1]) * u)
            return false
        }
    }
    return true
}

const fillPoly = (ctx: CanvasRenderingContext2D, pts: Pt[], style: string | CanvasGradient, alpha: number) => {
    if (alpha <= 0 || pts.length < 3) {
        return
    }
    ctx.globalAlpha = alpha
    ctx.fillStyle = style
    ctx.beginPath()
    ctx.moveTo(pts[0][0], pts[0][1])
    for (let i = 1; i < pts.length; i++) {
        ctx.lineTo(pts[i][0], pts[i][1])
    }
    ctx.closePath()
    ctx.fill()
}

interface InkOptions {
    closed?: boolean
    fill?: boolean
    tone?: number
    a?: number
    w?: number
}

/**
 * One stroke of line art, drawn `frac` of the way along. A filled shape is first knocked out with
 * the sky's own gradient, which is what keeps overlapping line work legible: the ship hides the
 * horizon behind it instead of being tangled up with it.
 */
const ink = (pen: Pen, pts: Pt[], drawn: number, o: InkOptions = {}) => {
    if (drawn <= 0 || pts.length < 2) {
        return
    }
    // Progress built from float timings lands a hair under 1 ((1 - 0.9) / 0.1 is 0.9999...), and a
    // closed shape only closes on a complete trace.
    const frac = drawn > 0.9995 ? 1 : drawn
    const { ctx } = pen
    if (o.fill) {
        const fa = smooth((frac - 0.45) / 0.55)
        fillPoly(ctx, pts, pen.sky, fa)
        if (o.tone) {
            fillPoly(ctx, pts, INK, fa * o.tone * (o.a ?? 1))
        }
    }
    ctx.globalAlpha = o.a ?? 1
    ctx.lineWidth = o.w ?? 1.1
    ctx.strokeStyle = INK
    ctx.beginPath()
    const done = trace(ctx, pts, frac, !!o.closed)
    if (done && o.closed) {
        ctx.closePath()
    }
    ctx.stroke()
}

const begin = (ctx: CanvasRenderingContext2D) => {
    ctx.globalAlpha = 1
    ctx.globalCompositeOperation = "source-over"
    ctx.lineCap = "round"
    ctx.lineJoin = "round"
}

// ---- the static layer: sky, horizon, moon, the far shore ---------------------------------------

export const paintStatic = (pen: Pen, t: number, land: Land, b: Bounds) => {
    const { ctx } = pen
    begin(ctx)
    ctx.fillStyle = pen.sky
    ctx.fillRect(b.left - 20, b.top - 20, b.right - b.left + 40, b.bottom - b.top + 40)

    const horizon = easeInOutCubic(prog(t, SCENE.horizon[0], SCENE.horizon[1]))
    ink(pen, [[b.left - 10, HORIZON], [b.right + 10, HORIZON]], horizon, { a: 0.42, w: 1 })

    const moon = easeInOutSine(prog(t, SCENE.moon[0], SCENE.moon[1]))
    if (moon > 0) {
        fillPoly(ctx, MOON_RIM, INK, 0.07 * smooth(prog(t, SCENE.moon[0] + 400, SCENE.moon[1] + 500)))
        ink(pen, MOON_RIM, moon, { a: 0.85, w: 1.15 })
        ink(pen, MOON_TERMINATOR, easeInOutSine(prog(t, SCENE.moon[0] + 500, SCENE.moon[1] + 300)), { a: 0.45, w: 0.8 })
        MOON_HATCH.forEach((h, i) =>
            ink(pen, h, easeInOutSine(prog(t, SCENE.moon[1] - 200 + i * 60, SCENE.moon[1] + 100 + i * 60)), { a: 0.32, w: 0.7 }),
        )
    }

    const shore = easeInOutSine(prog(t, SCENE.land[0], SCENE.land[0] + 1100))
    if (shore > 0) {
        fillPoly(ctx, land.poly, pen.sky, smooth((shore - 0.3) / 0.7))
        ink(pen, land.profile, shore, { a: 0.85, w: 1.2 })
        ink(pen, land.shore, easeInOutSine(prog(t, SCENE.land[0] + 300, SCENE.land[0] + 1300)), { a: 0.6, w: 0.9 })
        ink(pen, land.wetSand, easeInOutSine(prog(t, SCENE.land[0] + 500, SCENE.land[0] + 1500)), { a: 0.3, w: 0.8 })
        const hatchAt = SCENE.land[0] + 700
        land.hatches.forEach(h =>
            ink(pen, h.pts, easeInOutSine(prog(t, hatchAt + h.d * 700, hatchAt + h.d * 700 + 300)), { a: 0.38, w: 0.7 }),
        )
        land.pines.forEach(p => {
            const delay = Math.min(p.i, 6) * 140
            const f = easeInOutSine(prog(t, SCENE.land[0] + 900 + delay, SCENE.land[0] + 1500 + delay))
            ink(pen, p.pts, f, { closed: true, fill: true, a: 0.8, w: 1 })
        })
    }

    const rocks = easeInOutSine(prog(t, SCENE.rocks[0], SCENE.rocks[1]))
    if (rocks > 0) {
        for (const rock of [ROCK_SHORE_A, ROCK_SHORE_B]) {
            fillPoly(ctx, rock, pen.sky, smooth((rocks - 0.3) / 0.7))
            ink(pen, rock, rocks, { a: 0.85, w: 1.15 })
        }
        ink(pen, ROCK_SHORE_HATCH, easeInOutSine(prog(t, SCENE.rocks[0] + 400, SCENE.rocks[1])), { a: 0.45, w: 0.7 })
    }
}

// ---- the cloud across the moon (its own layer, so it can drift without repainting) -----------

export const paintCloud = (pen: Pen, t: number) => {
    begin(pen.ctx)
    const f = easeInOutSine(prog(t, SCENE.cloud[0], SCENE.cloud[1]))
    if (f <= 0) {
        return
    }
    const { lens, under } = cloudPoints(CLOUD.x, CLOUD.y)
    ink(pen, lens, f, { closed: true, fill: true, a: 0.32, w: 0.9 })
    ink(pen, under, f, { a: 0.2, w: 0.8 })
}

// ---- the live layer: the ship, the rocks it strikes, the water it disturbs ---------------------

const paintShipItself = (pen: Pen, t: number, st: ShipState) => {
    const { ctx } = pen
    const widthScale = 0.75 + 0.25 * (st.s / SHIP_AGROUND.s)

    // Nothing of the ship shows below the water.
    ctx.save()
    ctx.beginPath()
    ctx.rect(-400, -400, 1600, st.wl + 400.5)
    ctx.clip()
    for (const part of SHIP_PARTS) {
        const f = clamp01((st.ink - part.ink[0]) / (part.ink[1] - part.ink[0]))
        if (f <= 0) {
            continue
        }
        const source = part.dyn ? part.dyn(st, t) : part.shape!.pts
        const closed = part.dyn ? !!part.closed : part.shape!.closed
        const pts = source.map(p => {
            const q = part.pre ? part.pre(p[0], p[1], st) : p
            return shipPoint(st, q[0], q[1], part.rig)
        })
        const a = (part.a ?? 1) * st.alpha * (part.alphaFn ? part.alphaFn(st) : 1)
        ink(pen, pts, easeInOutSine(f), { closed, fill: part.fill, tone: part.tone, a, w: (part.w ?? 1.1) * widthScale })
    }
    ctx.restore()

    const drawn = smooth((st.ink - 0.3) / 0.3)
    const bow = shipPoint(st, 75, 0)
    const stern = shipPoint(st, -73, 0)
    if (drawn > 0) {
        // A ruffle of foam where the hull meets the water.
        const foam: Pt[] = []
        for (let i = 0; i <= 24; i++) {
            const x = lerp(stern[0], bow[0], i / 24)
            foam.push([x, st.wl + 0.6 + 0.7 * Math.sin(x * 0.45 + t / 260)])
        }
        ink(pen, foam, 1, { a: 0.55 * drawn * st.alpha, w: 0.9 })
    }

    // Under way: a bow wave and a wake, gone the moment she strikes.
    const way = prog(t, SCENE.sail[0], SCENE.sail[0] + 700) * (1 - prog(t, SCENE.impact, SCENE.impact + 450)) * drawn
    if (way > 0) {
        const j = Math.sin(t / 90) * 0.6
        ink(pen, [[bow[0] + 1, st.wl], [bow[0] - 5 * st.s, st.wl + 3 + j], [bow[0] - 16 * st.s, st.wl + 4.5]], 1, { a: 0.6 * way, w: 0.9 })
        ink(pen, [[bow[0] + 2, st.wl + 1], [bow[0] - 3 * st.s, st.wl + 7 * st.s]], 1, { a: 0.4 * way, w: 0.8 })
        ctx.setLineDash([3, 5])
        ctx.lineDashOffset = -t / 25
        ink(pen, [[stern[0] - 2, st.wl + 1], [stern[0] - 46 * st.s, st.wl - 1.5]], 1, { a: 0.45 * way, w: 0.8 })
        ink(pen, [[stern[0] - 2, st.wl + 2.5], [stern[0] - 38 * st.s, st.wl + 4 * st.s]], 1, { a: 0.35 * way, w: 0.8 })
        ctx.setLineDash([])
    }
}

/** Rings spreading from the bow at the strike. Drawn before the ship, so the hull hides their far side. */
const paintImpactRings = (ctx: CanvasRenderingContext2D, t: number) => {
    const dt = t - SCENE.impact
    if (dt < 0) {
        return
    }
    ctx.strokeStyle = INK
    ctx.lineWidth = 0.9
    for (let j = 0; j < 3; j++) {
        const d = dt - j * 160
        if (d < 0 || d > 1500) {
            continue
        }
        const e = easeOutCubic(d / 1500)
        const rx = 6 + 52 * e
        ctx.globalAlpha = 0.55 * (1 - e)
        ctx.beginPath()
        ctx.ellipse(CONTACT[0], CONTACT[1] + 2, rx, rx * 0.17, 0, 0, Math.PI * 2)
        ctx.stroke()
    }
}

/** Once she is fast, the swell keeps rolling in toward the rocks. */
const paintSwell = (pen: Pen, t: number) => {
    const dt = t - SCENE.impact - 900
    if (dt < 0) {
        return
    }
    const fade = smooth(dt / 800)
    for (let j = 0; j < 3; j++) {
        const u = ((dt + j * 1200) % 3600) / 3600
        const x0 = -10 + 120 * u
        const y = 405 + j * 9 - 4 * u
        ink(pen, flat(`M ${x0} ${y} Q ${x0 + 13} ${y - 2} ${x0 + 26} ${y}`, 8).pts, 1, { a: Math.sin(Math.PI * u) * 0.42 * fade, w: 0.85 })
    }
}

const paintRocks = (pen: Pen, t: number) => {
    const f = easeInOutSine(prog(t, SCENE.rocks[0], SCENE.rocks[1]))
    if (f <= 0) {
        return
    }
    for (const rock of [ROCK_BOW, ROCK_NEAR]) {
        fillPoly(pen.ctx, rock, pen.sky, smooth((f - 0.3) / 0.7))
        ink(pen, rock, f, { a: 0.85, w: 1.15 })
    }
    ROCK_BOW_HATCH.forEach((h, i) =>
        ink(pen, h, easeInOutSine(prog(t, SCENE.rocks[0] + 400 + i * 80, SCENE.rocks[1] + i * 80)), { a: 0.45, w: 0.7 }),
    )
    const foam = smooth(prog(t, SCENE.rocks[1], SCENE.rocks[1] + 600))
    if (foam > 0) {
        const l = 7 + 3 * Math.sin(t / 900)
        ink(pen, [[127 - l, 401], [126, 401]], 1, { a: 0.35 * foam, w: 0.8 })
        ink(pen, [[100 - l * 0.6, 401.5], [103, 401.5]], 1, { a: 0.25 * foam, w: 0.8 })
    }
}

const paintSpray = (ctx: CanvasRenderingContext2D, t: number) => {
    ctx.strokeStyle = INK
    for (const drop of sprayAt(t)) {
        ctx.globalAlpha = drop.alpha
        ctx.lineWidth = drop.width
        ctx.beginPath()
        ctx.moveTo(drop.from[0], drop.from[1])
        ctx.lineTo(drop.to[0], drop.to[1])
        ctx.stroke()
    }
}

/** The lantern's frame and its lit pane. The light it throws is an additive sprite in the scene. */
const paintLantern = (pen: Pen, t: number, st: ShipState) => {
    const lantern = lanternAt(st, t)
    if (lantern.drawn <= 0) {
        return
    }
    ink(pen, lantern.bracket, lantern.drawn, { a: 0.9 * st.alpha, w: 1 })
    if (lantern.lit > 0) {
        fillPoly(pen.ctx, lantern.body, "#ffc56b", lantern.lit * (0.75 + 0.25 * lantern.flicker))
    }
    ink(pen, lantern.body, lantern.drawn, { closed: true, a: 0.95 * st.alpha, w: 0.9 })
    ink(pen, lantern.cap, lantern.drawn, { a: 0.95 * st.alpha, w: 0.9 })
}

/** Everything that moves. Returns the ship's state, which the scene also needs for the lantern light. */
export const paintShip = (pen: Pen, t: number): ShipState => {
    const { ctx } = pen
    begin(ctx)
    const st = shipStateAt(t)
    paintImpactRings(ctx, t)
    paintSwell(pen, t)
    if (st.ink > 0) {
        paintShipItself(pen, t, st)
    }
    paintRocks(pen, t)
    paintSpray(ctx, t)
    paintLantern(pen, t, st)
    ctx.globalAlpha = 1
    return st
}
